/**
 * Fiat currency conversion for Cashero.
 *
 * Fetches XMR price from Kraken's public ticker API (no API key needed).
 * All balance multiplication happens locally — no third party sees user balances.
 */

import { PICONERO_PER_XMR } from './units';

/** A supported fiat currency */
export interface FiatCurrency {
  code: string;
  symbol: string;
  name: string;
  krakenPair: string;
}

/** Supported fiat currencies with their Kraken trading pairs */
export const SUPPORTED_FIAT: FiatCurrency[] = [
  { code: 'USD', symbol: '$', name: 'US Dollar', krakenPair: 'XMRUSD' },
  { code: 'EUR', symbol: '€', name: 'Euro', krakenPair: 'XMREUR' },
  { code: 'GBP', symbol: '£', name: 'British Pound', krakenPair: 'XMRGBP' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', krakenPair: 'XMRJPY' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', krakenPair: 'XMRAUD' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', krakenPair: 'XMRCAD' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc', krakenPair: 'XMRCHF' },
];

/** Get a FiatCurrency entry by its code */
export function getFiatCurrency(code: string): FiatCurrency {
  return SUPPORTED_FIAT.find(f => f.code === code) ?? SUPPORTED_FIAT[0];
}

/** Default fiat currency */
export const DEFAULT_FIAT = 'USD';

/**
 * Fetch the XMR price in a given fiat currency from Kraken's public ticker API.
 * Routes through the CORS proxy to ensure browser compatibility.
 *
 * @param fiatCode - Currency code (e.g. "USD", "EUR")
 * @returns Price of 1 XMR in the fiat currency, or null on failure
 */
export async function getXmrPrice(fiatCode: string): Promise<number | null> {
  const currency = getFiatCurrency(fiatCode);
  if (!currency.krakenPair) return null;

  const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';
  const targetUrl = encodeURIComponent(
    `https://api.kraken.com/0/public/Ticker?pair=${currency.krakenPair}`,
  );

  try {
    const response = await fetch(CORS_PROXY + targetUrl, {
      signal: AbortSignal.timeout(10000),
    });
    if (!response.ok) return null;

    const data = await response.json();
    if (data.error?.length > 0) return null;

    // Kraken returns results keyed by the pair name (e.g. XXMRZUSD)
    const result = Object.values(data.result ?? {})[0] as any;
    if (!result?.c?.[0]) return null;

    return parseFloat(result.c[0]);
  } catch {
    return null;
  }
}

/**
 * Convert a piconero balance to a fiat value string.
 *
 * @param piconero - Amount in piconero
 * @param xmrPrice - Price of 1 XMR in the fiat currency
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted fiat string (e.g. "$150.50")
 */
export function piconeroToFiat(piconero: number, xmrPrice: number, decimals: number = 2): string {
  const xmr = piconero / PICONERO_PER_XMR;
  const fiat = xmr * xmrPrice;
  return fiat.toFixed(decimals);
}

/**
 * Convert a fiat amount to piconero.
 *
 * @param fiatAmount - Amount in fiat currency (e.g. 150.50)
 * @param xmrPrice - Price of 1 XMR in the fiat currency
 * @returns Amount in piconero
 */
export function fiatToPiconero(fiatAmount: number, xmrPrice: number): number {
  const xmr = fiatAmount / xmrPrice;
  return Math.round(xmr * PICONERO_PER_XMR);
}

/**
 * Format a piconero amount as fiat with symbol.
 *
 * @param piconero - Amount in piconero
 * @param xmrPrice - Price of 1 XMR in the fiat currency
 * @param symbol - Fiat currency symbol (e.g. "$")
 * @returns Formatted string (e.g. "$150.50")
 */
export function formatFiat(piconero: number, xmrPrice: number, symbol: string = '$'): string {
  const value = piconeroToFiat(piconero, xmrPrice);
  return `${symbol}${value}`;
}
